/* eslint-disable react/prop-types */
import { ComponentType, ReactNode, useEffect } from 'react'

import { LOCALE } from '@hhgtech/hhg-components/types'
import { setupUnifyEnv } from '@hhgtech/hhg-components/unifyEnv'
import type { NextPage } from 'next'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import Router from 'next/router'
import NProgress from 'nprogress' //nprogress module

import 'components/templates/appWrappers/appDayJsInit'
import { AppHeightListener } from 'hooks/useAppHeight'
import { useTrackingVisitSite } from 'hooks/useCustomEventTracking'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { ScreenSizeListener, ScreenSizeProvider } from 'hooks/useScreenSize'
import { useServerLogs } from 'hooks/useServerLogs'
import { WaitInteractionListener } from 'hooks/useWaitInteraction'
import { CATEGORIES_TO_DISABLE_INITIAL_LOAD_ADS } from 'interfaces/constants/ads'
;(async () => {
  if (typeof window !== 'undefined' && !('IntersectionObserver' in window))
    // @ts-ignore
    await import('intersection-observer')
})()

setupUnifyEnv({
  DISCOVER_API: process.env.NEXT_PUBLIC_BASE_API_URL,
  INTERNAL_DISCOVER_API: process.env.WP_INTERNAL_API,
  TOGETHER_API: process.env.NEXT_PUBLIC_TOGETHER_API,
  // INTERNAL_TOGETHER_API: process.env.INTERNAL_TOGETHER_API,
  CARE_API: process.env.NEXT_PUBLIC_CARE_API,
  // INTERNAL_CARE_API: process.env.INTERNAL_CARE_API,
  SIDIS_TEMPLATE_API: process.env.NEXT_PUBLIC_SIDIS_TEMPLATE_API,
  SIDIS_API: process.env.NEXT_PUBLIC_SIDIS_API,
  SIDIS_API_GET: process.env.NEXT_PUBLIC_SIDIS_API_GET,
  SSO_API: process.env.NEXT_PUBLIC_SSO,
  LOCALE: process.env.NEXT_PUBLIC_DEPLOY_LOCALE,
  DEPLOY_ENV: process.env.NEXT_PUBLIC_DEPLOY_ENV,
  LEAD_API: process.env.NEXT_PUBLIC_LEAD_API,
  SUBOT_API: process.env.NEXT_PUBLIC_SUBOT_API,
  INTERNAL_SUBOT_API: process.env.SUBOT_INTERNAL,
  SUBOT_URL: process.env.NEXT_PUBLIC_SUBOT_URL,
})

if (typeof window !== 'undefined' && history.scrollRestoration) {
  history.scrollRestoration = 'manual'
}

export type NextPageWithLayout = NextPage<{
  err?: Error
}> & {
  LayoutComponent?: ComponentType<{
    children: ReactNode
    pageProps?: AppProps['pageProps']
  }>
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
  err?: Error
}

const AppWrapper = dynamic(() => import('components/templates/appWrappers'))
// const EasterEggs = dynamic(() =>
//   import('components/atoms/easterEggs').then((mod) => mod.EasterEggs),
// )

const usingRUM =
  process.env.NEXT_PUBLIC_DEPLOY_ENV === 'production' &&
  process.env.NEXT_PUBLIC_DEPLOY_LOCALE === 'vi-VN'

// declare let google: any
export default function AppLegacyWrapper({
  Component,
  pageProps,
  err,
}: AppPropsWithLayout) {
  const { locale, asPath } = useRouterExtend()

  useServerLogs(pageProps)
  useTrackingVisitSite()
  // setup nprogress (thin blue loading bar top of the page)
  useEffect(() => {
    NProgress.configure({ showSpinner: false })
    setTimeout(() => {
      Router.events.on('routeChangeStart', () => NProgress.start())
    }, 100)
    Router.events.on('routeChangeComplete', () => NProgress.done())
    Router.events.on('routeChangeError', () => NProgress.done())
  }, [])
  // Use the layout defined at the page level, if available
  const LayoutComponent = Component.LayoutComponent || AppWrapper
  return (
    <>
      <WaitInteractionListener />
      {/* {process.env.NEXT_PUBLIC_DEPLOY_ENV !== 'production' &&
        locale === 'vi-VN' && <EasterEggs />} */}
      <Head>
        {/* {locale === 'id-ID' && (
          <script
            async
            src="https://scripts.jixie.media/onescript/He5951FCE1/jx-He232595rbHQ4.min.js"
          ></script>
        )}
        {locale === 'id-ID' && asPath.startsWith('/obat-suplemen/') && (
          <script
            data-cfasync="false"
            type="text/javascript"
            dangerouslySetInnerHTML={{
              __html: `
              (function(w, d) {
                var s = d.createElement('script');
                s.src = '//cdn.adpushup.com/44803/adpushup.js';
                s.crossOrigin='anonymous';
                s.type = 'text/javascript'; s.async = true;
                (d.getElementsByTagName('head')[0] || d.getElementsByTagName('body')[0]).appendChild(s);
                w.adpushup = w.adpushup || {que:[]};
              })(window, document);
              `,
            }}
          ></script>
        )} */}
        <script
          id="gpt-init"
          key="gpt-init"
          async
          dangerouslySetInnerHTML={{
            __html: `window.googletag = window.googletag || { cmd: [] };
              googletag.cmd.push(function () {
                googletag.pubads().collapseEmptyDivs(true);
                ${
                  locale !== LOCALE.Vietnam
                    ? 'googletag.pubads().enableSingleRequest();'
                    : ''
                }
                ${
                  (
                    CATEGORIES_TO_DISABLE_INITIAL_LOAD_ADS[locale] || []
                  ).find((i: string) => asPath.startsWith(i))
                    ? 'googletag.pubads().disableInitialLoad();'
                    : ''
                }

                googletag.pubads().enableLazyLoad({
                  fetchMarginPercent: 300,
                  renderMarginPercent: ${locale === LOCALE.Vietnam ? 50 : 30},
                  mobileScaling: 2.0,
                });
                googletag.enableServices();
                if (window.location.hostname.startsWith('demo.')) {
                  googletag.pubads().setTargeting("demo",["yes"]);
                }
                if (window.location.pathname.startsWith('/preview/')) {
                  googletag.pubads().setTargeting("preview",["yes"]);
                }
              });
              `,
          }}
        />

        {usingRUM && (
          <script
            id="debug-bear-rum"
            key="debug-bear-rum"
            async
            dangerouslySetInnerHTML={{
              __html: `(function () {
                const dbpr = 100;
                if (Math.random() * 100 > 100 - dbpr) {
                  const d = 'dbbRum', w = window, o = document, a = addEventListener, scr = o.createElement('script');
                  scr.async = true;
                  w[d] = w[d] || [];
                  w[d].push(['presampling', dbpr]);
                  ['error', 'unhandledrejection'].forEach(function (t) {
                    a(t, function (e) {
                      w[d].push([t, e]);
                    });
                  });
                  scr.src = 'https://cdn.debugbear.com/${process.env.NEXT_PUBLIC_DEBUGBEAR}.js';
                  o.head.appendChild(scr);
                }
              })();`,
            }}
          />
        )}
      </Head>
      <ScreenSizeProvider isMobileSSR={pageProps?.isMobileSsr}>
        <ScreenSizeListener />
        <AppHeightListener />
        <LayoutComponent pageProps={pageProps}>
          <Component {...pageProps} err={err} />
        </LayoutComponent>
      </ScreenSizeProvider>
    </>
  )
}
